// Creates a view with microscope sorter on one side and notebook manual on the other side
import * as React from "react";
import { useSelector } from "react-redux";

import { FullscreenModal } from "@learningblocks/storybook-components/lib/FullscreenModal";
import { SimManager } from "@learningblocks/simulation-engine";

import { NotebookManual } from "../../NotebookManual";
import { NotebookManualProps } from "../../NotebookManual/types";
import {
  MicroscopeSorter,
  MicroscopeSorterProps,
  SortedItem,
} from "../../MicroscopeSorter";
import * as styles from "./MicroscopeSorterNotebookModal.css";

interface MicroscopeSorterNotebookModalProps {
  isOpen: boolean;
  onClose: () => void;
  notebookCollapsed: boolean;
  data: {
    notebookData: NotebookManualProps;
    microscopeSorterData: MicroscopeSorterProps;
  };
}

// Type definitions for notebook component data structure
interface TableCell {
  type: "TITLE" | "FIXED" | "INPUT";
  text: string;
}

type TableRow = TableCell[];

type TableData = TableRow[];

interface DataTableComponent {
  id: string;
  type: "DATA_TABLE";
  data: TableData;
  [key: string]: any;
}

// Constants for data structure indices
const NOTEBOOK_CONSTANTS = {
  FIRST_PAGE_INDEX: 0,
  TABLE_COMPONENT_INDEX: 1,
  PARENTAL_ROW_INDEX: 1,
  RECOMBINANT_ROW_INDEX: 2,
  COUNT_CELL_INDEX: 1,
} as const;

// Type guard to check if component is a DataTable
function isDataTableComponent(component: any): component is DataTableComponent {
  return (
    component &&
    typeof component === "object" &&
    component.type === "DATA_TABLE" &&
    Array.isArray(component.data)
  );
}

// Safely update table cell text
function updateTableCell(
  data: TableData,
  rowIndex: number,
  cellIndex: number,
  newText: string
): boolean {
  if (
    data[rowIndex] &&
    data[rowIndex][cellIndex] &&
    typeof data[rowIndex][cellIndex].text === "string"
  ) {
    data[rowIndex][cellIndex].text = newText;
    return true;
  }
  return false;
}

export function MicroscopeSorterNotebookModal(
  props: MicroscopeSorterNotebookModalProps
) {
  let sortedItems: SortedItem[] = [];
  let hasRedux = false;
  sortedItems = useSelector(
    (state: any) => state.microscopeSorter?.sortedItems || []
  );
  if (sortedItems && Array.isArray(sortedItems) && sortedItems.length > 0) {
    hasRedux = true;
  } else {
    hasRedux = false;
  }

  // Fallback to local state when Redux is not available (non-persisted)
  const [localParentalCount, setLocalParentalCount] = React.useState(0);
  const [localRecombinantCount, setLocalRecombinantCount] = React.useState(0);

  // Handle microscope state when modal opens
  React.useEffect(() => {
    if (props.isOpen) {
      const state = SimManager.getInstance().getState("microscopeMeiosis");
      if (state === "control") {
        SimManager.getInstance().setInputValue("microscopeMeiosis", {
          controlSlideVisited: true,
        });
      }
    }
  }, [props.isOpen]);

  const handleCountsChange = React.useCallback(
    (newParentalCount: number, newRecombinantCount: number) => {
      if (!hasRedux) {
        setLocalParentalCount(newParentalCount);
        setLocalRecombinantCount(newRecombinantCount);
      }
    },
    [hasRedux]
  );

  const handleSubmitComplete = React.useCallback(() => {
    // Close modal and return to lab after all questions are answered
    props.onClose();
  }, [props]);

  // Calculate counts from Redux state (persisted) or local state (non-persisted)
  const parentalCount = React.useMemo(() => {
    if (hasRedux) {
      return sortedItems.filter((item) => item.selectedOption === "parental")
        .length;
    }
    return localParentalCount;
  }, [hasRedux, sortedItems, localParentalCount]);

  const recombinantCount = React.useMemo(() => {
    if (hasRedux) {
      return sortedItems.filter((item) => item.selectedOption === "recombinant")
        .length;
    }
    return localRecombinantCount;
  }, [hasRedux, sortedItems, localRecombinantCount]);

  // Create updated notebook data with counts (persisted if Redux available, otherwise local)
  const updatedNotebookData = React.useMemo(() => {
    const notebookData = { ...props.data.notebookData };

    // Validate structure exists
    const firstPage = notebookData.pages?.[NOTEBOOK_CONSTANTS.FIRST_PAGE_INDEX];

    if (
      !notebookData.pages ||
      !firstPage ||
      !firstPage.components ||
      !firstPage.components[NOTEBOOK_CONSTANTS.TABLE_COMPONENT_INDEX]
    ) {
      console.warn(
        "MicroscopeSorterNotebookModal: Expected notebook structure not found"
      );
      return notebookData;
    }

    const tableComponent =
      firstPage.components[NOTEBOOK_CONSTANTS.TABLE_COMPONENT_INDEX];

    // Verify it's a data table component
    if (!isDataTableComponent(tableComponent)) {
      console.warn(
        "MicroscopeSorterNotebookModal: Component at index is not a DATA_TABLE"
      );
      return notebookData;
    }

    // Deep clone to avoid mutation using structuredClone (modern replacement for JSON.parse/stringify)
    const pages = structuredClone(notebookData.pages);
    const clonedFirstPage = pages[NOTEBOOK_CONSTANTS.FIRST_PAGE_INDEX];

    if (!clonedFirstPage?.components) {
      return notebookData;
    }

    const clonedTableComponent =
      clonedFirstPage.components[NOTEBOOK_CONSTANTS.TABLE_COMPONENT_INDEX];

    if (!isDataTableComponent(clonedTableComponent)) {
      return notebookData;
    }

    // Update parental count
    const parentalUpdated = updateTableCell(
      clonedTableComponent.data,
      NOTEBOOK_CONSTANTS.PARENTAL_ROW_INDEX,
      NOTEBOOK_CONSTANTS.COUNT_CELL_INDEX,
      String(parentalCount)
    );

    // Update recombinant count
    const recombinantUpdated = updateTableCell(
      clonedTableComponent.data,
      NOTEBOOK_CONSTANTS.RECOMBINANT_ROW_INDEX,
      NOTEBOOK_CONSTANTS.COUNT_CELL_INDEX,
      String(recombinantCount)
    );

    if (!parentalUpdated || !recombinantUpdated) {
      console.warn(
        "MicroscopeSorterNotebookModal: Could not update table cells"
      );
    }

    return { ...notebookData, pages };
  }, [props.data.notebookData, parentalCount, recombinantCount]);

  return (
    <FullscreenModal
      className={styles.modalContainer}
      isOpen={props.isOpen}
      closeButton={true}
      notebookExpanded={!props.notebookCollapsed}
      close={props.onClose}
      backLabel={"Lab"}
    >
      <div className={styles.modalLayout}>
        <div className={styles.microscopeContainer}>
          <MicroscopeSorter
            {...props.data.microscopeSorterData}
            onCountsChange={handleCountsChange}
            onSubmit={handleSubmitComplete}
          />
        </div>
        <div className={styles.notebookContainer}>
          <NotebookManual {...updatedNotebookData} />
        </div>
      </div>
    </FullscreenModal>
  );
}
